<template>
  <div
    v-if="report"
    class="overflow-hidden group mx-auto flex h-[375px] xl:h-[272px] w-full flex-col xl:flex-row justify-between rounded-lg bg-white transition-shadow hover:shadow-lg border border-grey-600"
    data-testid="report-card"
  >
    <div
      class="relative h-[96px] xl:h-full w-full xl:max-w-[127px] flex-shrink-0 rounded-t-lg xl:rounded-t-none xl:rounded-l-lg bg-s-300"
    >
      <img
        v-if="reportImage"
        :src="reportImage"
        class="flex size-full xl:max-h-full h-[96px] xl:h-full rounded-t-lg xl:rounded-t-none xl:rounded-l-lg object-cover"
        alt="Report forecasts"
        data-testid="report-card-image"
      />
    </div>
    <div class="flex w-full flex-col justify-between p-6 gap-y-1 h-full">
      <div class="flex flex-col items-start justify-between gap-1">
        <div class="flex flex-col gap-y-2">
          <div class="flex items-center gap-x-2 min-h-6 flex-wrap gap-y-1">
            <span
              v-if="isNew"
              class="bg-p-500 rounded-full px-4 py-1 text-xs font-semibold"
            >
              New
            </span>
            <p class="text-s-600 whitespace-nowrap text-xs font-semibold">
              <span data-testid="report-card-published-date">
                {{ formattedDate }}
              </span>
              <span
                v-if="Boolean(market)"
                data-testid="report-card-market-name"
                class="capitalize"
              >
                | {{ market }}
              </span>
            </p>
          </div>
          <h3
            class="line-clamp-4 xl:line-clamp-2 text-lg font-semibold xl:text-xl !leading-[normal]"
            data-testid="report-card-title"
            v-html="reportTitle"
          />
          <span
            v-if="description"
            class="line-clamp-2 xl:line-clamp-4"
            v-html="description"
          />
        </div>
      </div>
      <ReportFooter
        :report="report"
        @open-viewer="$emit('open-viewer', $event)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import ReportFooter from '~/components/critical-insights/ReportFooter.vue'
import { format } from 'date-fns'
import type { TReport } from '~/src/types/reports'

interface Props {
  report?: TReport
  market?: string
}

const props = withDefaults(defineProps<Props>(), {
  report: () => null,
  market: '',
})

defineEmits(['open-viewer'])

const isNew = computed(() => {
  const today = new Date()
  const reportDate = new Date(props.report?.date)
  const diffTime = Math.abs(today.getTime() - reportDate.getTime())
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  return diffDays <= 15
})

const reportTitle = computed(() => props.report?.title)
const description = computed(() => props.report?.blurb)
const reportImage = computed(() => {
  if (
    window?.matchMedia('(min-width: 1280px) and (max-width: 7680px)').matches
  ) {
    return props.report?.images?.desktop || props.report?.images?.mobile
  }
  return props.report?.images?.mobile || props.report?.images?.desktop
})
const formattedDate = computed(() =>
  format(new Date(props.report?.date), 'dd MMM yyyy'),
)
</script>
